import * as THREE from 'three';

// Tunable Wedge Constants
const WEDGE_LENGTH = 0.22; // 22cm total length
const WEDGE_WIDTH = 0.08; // 8cm wide
const WEDGE_MAX_THICKNESS = 0.065; // 6.5cm thick at butt (high-lift bucking wedge)
const WEDGE_MIN_THICKNESS = 0.005; // 5mm thin at tip
const WEDGE_COLOR = 0xf59e0b; // Vibrant safety yellow-amber
const WEDGE_ROUGHNESS = 0.4;
const WEDGE_METALNESS = 0.1;

/**
 * Creates a procedural 3D mesh for a forestry felling/bucking wedge.
 * Local coordinate system:
 * - Local X: Thickness axis (separates endcaps, T_min at tip to T_max at butt)
 * - Local Y: Lateral width axis (along the cut width)
 * - Local Z: Length axis (Tip at -Z, Butt at +Z)
 */
export function createWedgeGeometry(): THREE.BufferGeometry {
  const halfL = WEDGE_LENGTH * 0.5;
  const halfW = WEDGE_WIDTH * 0.5;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  // Helper to add a quad face with computed flat normal
  const addQuad = (
    p0: [number, number, number],
    p1: [number, number, number],
    p2: [number, number, number],
    p3: [number, number, number]
  ) => {
    const v0 = new THREE.Vector3(...p0);
    const v1 = new THREE.Vector3(...p1);
    const v2 = new THREE.Vector3(...p2);
    
    // Normal = (v1 - v0) x (v2 - v0)
    const normal = new THREE.Vector3()
      .subVectors(v1, v0)
      .cross(new THREE.Vector3().subVectors(v2, v0))
      .normalize();

    const baseIdx = positions.length / 3;

    positions.push(...p0, ...p1, ...p2, ...p3);
    for (let i = 0; i < 4; i++) {
      normals.push(normal.x, normal.y, normal.z);
    }
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);

    indices.push(baseIdx, baseIdx + 1, baseIdx + 2);
    indices.push(baseIdx, baseIdx + 2, baseIdx + 3);
  };

  // 8 Primary Vertices of the Flat-Sided Wedge
  // The -X face is completely flat (x = 0) to sit flush against the cut endcap without twisting or penetrating.
  // The +X face tapers from WEDGE_MIN_THICKNESS at tip to WEDGE_MAX_THICKNESS at butt to push the kerf open.
  
  // Tip Vertices (z = -halfL)
  const vTipNegXNegY: [number, number, number] = [0, -halfW, -halfL];
  const vTipPosXNegY: [number, number, number] = [WEDGE_MIN_THICKNESS, -halfW, -halfL];
  const vTipPosXPosY: [number, number, number] = [WEDGE_MIN_THICKNESS, halfW, -halfL];
  const vTipNegXPosY: [number, number, number] = [0, halfW, -halfL];

  // Butt Vertices (z = +halfL)
  const vButtNegXNegY: [number, number, number] = [0, -halfW, halfL];
  const vButtPosXNegY: [number, number, number] = [WEDGE_MAX_THICKNESS, -halfW, halfL];
  const vButtPosXPosY: [number, number, number] = [WEDGE_MAX_THICKNESS, halfW, halfL];
  const vButtNegXPosY: [number, number, number] = [0, halfW, halfL];

  // 1. +X Face (Right sloped face pushing opposing log endcap)
  addQuad(vTipPosXNegY, vButtPosXNegY, vButtPosXPosY, vTipPosXPosY);

  // 2. -X Face (Flat reference face flush against reference log endcap)
  addQuad(vButtNegXNegY, vTipNegXNegY, vTipNegXPosY, vButtNegXPosY);

  // 3. +Y Face (Top edge)
  addQuad(vTipPosXPosY, vButtPosXPosY, vButtNegXPosY, vTipNegXPosY);

  // 4. -Y Face (Bottom edge)
  addQuad(vButtPosXNegY, vTipPosXNegY, vTipNegXNegY, vButtNegXNegY);

  // 5. -Z Face (Slim Front Tip)
  addQuad(vTipPosXNegY, vTipPosXPosY, vTipNegXPosY, vTipNegXNegY);

  // 6. +Z Face (Thick Back Butt)
  addQuad(vButtPosXPosY, vButtPosXNegY, vButtNegXNegY, vButtNegXPosY);

  // Add Grip Ribs on the sloped +X face only (keeping -X completely flat against the endcap)
  const numRibs = 5;
  const ribHeight = 0.002;
  const ribWidth = 0.006;

  for (let r = 1; r <= numRibs; r++) {
    const zCenter = -halfL + (r / (numRibs + 1)) * WEDGE_LENGTH;
    const z0 = zCenter - ribWidth * 0.5;
    const z1 = zCenter + ribWidth * 0.5;
    const frac = (zCenter + halfL) / WEDGE_LENGTH;
    const tLocal = WEDGE_MIN_THICKNESS + (WEDGE_MAX_THICKNESS - WEDGE_MIN_THICKNESS) * frac;

    // Rib on +X face
    const ribPos0: [number, number, number] = [tLocal + ribHeight, -halfW * 0.8, z0];
    const ribPos1: [number, number, number] = [tLocal + ribHeight, halfW * 0.8, z0];
    const ribPos2: [number, number, number] = [tLocal + ribHeight, halfW * 0.8, z1];
    const ribPos3: [number, number, number] = [tLocal + ribHeight, -halfW * 0.8, z1];
    addQuad(ribPos0, ribPos3, ribPos2, ribPos1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uvs), 2));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  return geometry;
}

export function createWedgeMaterial(): THREE.Material {
  return new THREE.MeshStandardMaterial({
    color: WEDGE_COLOR,
    roughness: WEDGE_ROUGHNESS,
    metalness: WEDGE_METALNESS,
    side: THREE.DoubleSide
  });
}

export function createWedgeMesh(name?: string): THREE.Mesh {
  const mesh = new THREE.Mesh(createWedgeGeometry(), createWedgeMaterial());
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  if (name) mesh.name = name;
  return mesh;
}
